import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ArrowLeft, History, RotateCcw, Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

export default function TheoryOfChange() {
  const { pid } = useParams();
  const [program, setProgram] = useState(null);
  const [doc, setDoc] = useState({ content: "", note: "", version: 0, versions_count: 0 });
  const [draft, setDraft] = useState({ content: "", note: "" });
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    const [p, t, v] = await Promise.all([
      api.get(`/programs/${pid}`),
      api.get(`/programs/${pid}/theory-of-change`),
      api.get(`/programs/${pid}/theory-of-change/versions`),
    ]);
    setProgram(p.data);
    setDoc(t.data);
    setDraft({ content: t.data.content || "", note: "" });
    setVersions(v.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const dirty = (draft.content || "") !== (doc.content || "");

  const save = async () => {
    if (!dirty) {
      toast.info("Nothing changed.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/programs/${pid}/theory-of-change`, {
        content: draft.content,
        note: draft.note,
      });
      setDoc(data);
      setDraft({ content: data.content || "", note: "" });
      toast.success(`Saved version ${data.version}`);
      const v = await api.get(`/programs/${pid}/theory-of-change/versions`);
      setVersions(v.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generateAi = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/programs/${pid}/theory-of-change/generate`);
      const text = data?.narrative || data?.text || "";
      if (text) {
        setDraft({ content: text, note: "AI-drafted" });
        toast.success("Theory of Change drafted by AI — review and Save when ready.");
      } else {
        toast.error("AI returned no content.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI generation failed (rate limit?)");
    } finally {
      setGenerating(false);
    }
  };

  const restore = async (version) => {
    try {
      const { data } = await api.post(`/programs/${pid}/theory-of-change/versions/${version.id}/restore`);
      setDoc(data);
      setDraft({ content: data.content || "", note: "" });
      toast.success(`Restored v${version.version} as v${data.version}`);
      const v = await api.get(`/programs/${pid}/theory-of-change/versions`);
      setVersions(v.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <Link to={`/programs/${pid}`} className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900" data-testid="toc-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> {program?.name || "Program"}
        </Link>
        <p className="label-overline mt-3">Narrative</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold mt-1">Theory of Change</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          The cause-and-effect story behind your indicators. Every save creates a snapshot you can scroll
          through and restore at any time.
        </p>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center justify-between mb-3 gap-2">
                <p className="label-overline">
                  Working draft {doc.version > 0 && <span className="text-slate-400">· current v{doc.version}</span>}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateAi}
                  disabled={generating}
                  data-testid="toc-ai-generate"
                  title="Use Claude to draft a Theory of Change from your indicators and current data"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> {generating ? "Drafting…" : "AI: Draft"}
                </Button>
              </div>
              <Textarea
                value={draft.content}
                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                rows={18}
                className="font-mono text-sm"
                placeholder="If we... then... because..."
                data-testid="toc-content"
              />
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
                <div>
                  <Label className="label-overline">Save note (optional)</Label>
                  <Input
                    value={draft.note}
                    onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                    placeholder="What changed in this revision?"
                    className="mt-1"
                    data-testid="toc-note"
                  />
                </div>
                <div className="text-right pt-5">
                  <Button
                    onClick={save}
                    disabled={saving || !dirty}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="toc-save"
                  >
                    <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : dirty ? "Save new version" : "No changes"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <aside className="space-y-3">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <h2 className="font-heading text-xl font-semibold">Version history</h2>
            </div>
            {doc.version > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs" data-testid="toc-current-version">
                <p className="font-medium text-emerald-900">v{doc.version} · current</p>
                <p className="text-emerald-800 mt-1">
                  {doc.updated_by_name || "?"} · {doc.updated_at ? new Date(doc.updated_at).toLocaleString() : ""}
                </p>
                {doc.note && <p className="text-emerald-700 italic mt-1">"{doc.note}"</p>}
              </div>
            )}
            {versions.length === 0 ? (
              <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-4">
                No previous versions. Save a change and your prior copy will appear here.
              </div>
            ) : (
              <ol className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {versions.map((v) => (
                  <li
                    key={v.id}
                    className="bg-white border border-slate-200 rounded-sm p-3 text-xs"
                    data-testid={`toc-version-${v.version}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">v{v.version}</span>
                      <RestoreDialog version={v} onRestore={() => restore(v)} />
                    </div>
                    <p className="text-slate-500 mt-1">
                      {v.saved_by_name || "?"} · {new Date(v.saved_at).toLocaleString()}
                    </p>
                    {v.note && <p className="text-slate-600 italic mt-1">"{v.note}"</p>}
                    <p className="text-slate-700 mt-2 line-clamp-3 whitespace-pre-line">{v.content}</p>
                  </li>
                ))}
              </ol>
            )}
          </aside>
        </div>
      )}
    </Layout>
  );
}

function RestoreDialog({ version, onRestore }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`toc-restore-${version.version}`}>
          <RotateCcw className="w-3 h-3 mr-1" /> Restore
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Restore version {version.version}?</DialogTitle>
          <DialogDescription>
            Your current draft will be saved as a new version first, so nothing is lost.
          </DialogDescription>
        </DialogHeader>
        <pre className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-800 max-h-60 overflow-auto whitespace-pre-wrap">
          {version.content}
        </pre>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            className="bg-slate-900 hover:bg-slate-800 text-white"
            onClick={() => { setOpen(false); onRestore(); }}
            data-testid={`toc-restore-confirm-${version.version}`}
          >
            Restore
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
